/* eslint-disable no-underscore-dangle */
import React, {
  useState,
  useEffect,
} from 'react';
import i18n from 'i18next';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  useHistory,
  useLocation,
} from 'react-router-dom';
import {
  createFilter,
} from 'react-select';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  useClickAway,
} from 'web-hooks';
import {
  getUiState,
} from 'web-selectors';
import {
  uiActions,
} from 'web-actions';

import {
  Button,
  Select,
  Tooltip,
  AddExercise,
  AssignSearchOption,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

import * as S from './styled';

const mapState = state => {
  const exercises = coreSelectors.getEntitySearchOptions(
    'exercises',
    'library',
  )(state);

  return {
    options: [
      {
        newOne: true,
        value: 'new',
        label: 'CREATE NEW',
      },
      ...exercises,
    ],
    exerciseFolders: coreSelectors.getEntityFolders(
      'exercises',
    )(state),
    exercisePickerIsOpen: getUiState('exercisePickerIsOpen')(state),
  };
};

type Props = {
  workoutId: number,
  workoutType?: string,
};

const WorkoutExercisesFooter = ({
  workoutId,
  workoutType,
}: Props) => {
  const [visible, setVisible] = useState(false);
  const [newExerciseModal, setNewExerciseModal] = useState({
    isOpen: false,
  });

  const {
    options,
    exerciseFolders,
    exercisePickerIsOpen,
  } = useSelector(mapState, shallowEqual);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const ref = useClickAway(
    () => {
      setVisible(false);
      dispatch(uiActions.cancelExercisePicker());
    },
    {
      disabled: !visible,
    },
  );

  const onRequestAdd = (exerciseId) => {
    dispatch(
      coreActions.requestAddExerciseToWorkout({
        workoutId,
        exerciseId,
        workoutType,
        callbacks: {
          onSuccess: () => {
            const button = document.querySelector('.exercise-picker-button');
            button?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          },
        },
      }),
    );
  };

  useEffect(() => {
    if (exercisePickerIsOpen && !visible) {
      setVisible(true);
    }
    if (!exercisePickerIsOpen && visible) {
      setVisible(false);
    }
  }, [exercisePickerIsOpen]);

  useEffect(() => {
    const entity = location.state?.entity;

    if (entity?.id) {
      if (exercisePickerIsOpen) {
        dispatch(uiActions.selectExercisePicker(entity.id));
      } else {
        onRequestAdd(entity.id);
      }

      history.replace({
        pathname: location.pathname,
        state: {},
      });
    }
  }, [location]);

  const handleAddNewExercise = (payload, actions) => {
    const onSuccess = (res) => {
      setNewExerciseModal({
        isOpen: false,
      });

      history.push(`/exercises/${res.id}/edit?back=${location.pathname}`);
    };

    dispatch(
      coreActions.requestAddEntity({
        payload,
        resourceType: 'exercises',
        list: 'library',
        callbacks: {
          ...actions,
          onSuccess,
        },
      }),
    );
  };

  return (
    <S.Footer>
      <Flex flex="0 0 260px">
        {visible ? (
          <S.Search ref={ref}>
            <Select
              value=""
              autoFocus
              menuIsOpen
              creatable
              placeholder="Search exercises"
              menuPlacement="top"
              createOptionPosition="first"
              filterOption={createFilter({ ignoreAccents: false })}
              components={{ Option: AssignSearchOption }}
              formatCreateLabel={userInput => (
                <S.Label>
                  Add
                  {' '}
                  <b>{userInput}</b>
                </S.Label>
              )}
              options={options}
              styles={{
                menu: provided => ({
                  ...provided,
                  marginTop: '1px',
                }),
                menuList: provided => ({
                  ...provided,
                  maxHeight: '220px',
                }),
              }}
              onChange={(option) => {
                setVisible(false);
                if (!option) return;

                if (option.__isNew__ || option.newOne) {
                  setNewExerciseModal({
                    name: option.newOne ? '' : option.value,
                    isOpen: true,
                  });

                  return;
                }

                if (exercisePickerIsOpen) {
                  dispatch(uiActions.selectExercisePicker(+option.value));
                } else {
                  onRequestAdd(+option.value);
                }
              }}
            />
          </S.Search>
        ) : (
          <Tooltip
            body={i18n.t('tooltip.add.workout.exercise')}
            preferPlace="right"
          >
            <Button
              noPrint
              icon="fas fa-plus"
              appearance="success"
              className="exercise-picker-button"
              onClick={() => setTimeout(() => setVisible(true), 0)}
            >
              Add Exercise
            </Button>
          </Tooltip>
        )}
      </Flex>

      <AddExercise
        isOpen={newExerciseModal.isOpen}
        initialName={newExerciseModal.name}
        folders={exerciseFolders}
        onSubmit={handleAddNewExercise}
        onRequestClose={() => {
          setNewExerciseModal({
            isOpen: false,
          });
        }}
      />
    </S.Footer>
  );
};

WorkoutExercisesFooter.defaultProps = {
  workoutType: 'WorkoutTemplate',
};

export default WorkoutExercisesFooter;
